import { FC } from 'react';
import { Typography, Popover } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { differenceInCalendarDays } from 'date-fns';
import { Trans } from 'react-i18next';
import i18n from 'i18next';

import { SettingsCard } from './SettingsCard';
import { ProgressComponent } from 'componets/ui';

import { useAppSelector, useTranslate } from 'hooks';

import { LoyaltyItem } from 'models';

import { LoyaltyIcons } from 'utils/constants';

import Utils from 'utils/utils';

import { ReactComponent as BasicIcon } from 'assets/icons/basic-icon.svg';
import { ReactComponent as StarIcon } from 'assets/icons/star.svg';

import { selectUser } from 'selectors';

import s from './index.module.scss';

const DAYS = 90;

const TEXTS: { [key: string]: string } = {
	Silver: i18n.t('loyaltySilverText'),
	Gold: i18n.t('loyaltyGoldText'),
	Platinum: i18n.t('loyaltyPlatinumText'),
};

export const SettingLoyaltyCard: FC = () => {
	const { loyalty, data: overview } = useAppSelector((state) => state.overview);
	const user = useAppSelector(selectUser);

	const t = useTranslate([
		'yourStatusText',
		'loyaltyProgramText',
		'readMoreLoyaltyText',
		'unavailableLoyaltyStatusText',
		'dayText',
		'daysText',
		'loyaltyStatusTooltipText',
		'loyaltyStatusBasicText',
	]);

	const balance = overview.totalBalance;
	const currentPlan = user.category;

	const nextPlan = loyalty.find((plan: LoyaltyItem) => plan.locked) || loyalty[loyalty.length - 1];

	const plan = loyalty.find((item) => item.title === currentPlan.title);

	const isPlatinum = plan?.title === 'Platinum';
	const isBasic = !currentPlan?.isLoyalty && !currentPlan?.isDefault;

	const percent = isPlatinum
		? (+balance / plan?.minAmount) * 100
		: (+balance / nextPlan?.minAmount) * 100;

	const days = differenceInCalendarDays(new Date(), new Date(user.registrationDate));
	const isUnavailableLoyalty = days < DAYS;

	const Icon = plan ? LoyaltyIcons[plan?.title] : currentPlan?.slug === 'default' ? BasicIcon : '';

	const text = plan
		? TEXTS[plan?.title]
		: currentPlan?.slug === 'default'
		? t.loyaltyStatusBasicText
		: '';

	const statusLabel = (
		<Typography.Text className={s.text}>
			{t.yourStatusText}&nbsp;
			<Popover content={t.loyaltyStatusTooltipText} placement="top">
				<InfoCircleOutlined style={{ cursor: 'pointer' }} />
			</Popover>
		</Typography.Text>
	);

	return (
		<SettingsCard
			title={
				<div className={s.loyaltyTitle}>
					{t.loyaltyProgramText} <StarIcon />
				</div>
			}
			className="loyalty"
		>
			{isUnavailableLoyalty && !isBasic ? (
				<div>
					<Typography.Text
						type="secondary"
						style={{ marginBottom: '18px', fontSize: '16px', display: 'block' }}
					>
						{t.unavailableLoyaltyStatusText}
					</Typography.Text>

					<ProgressComponent percent={percent} />

					<Typography.Text
						type="secondary"
						style={{ marginTop: '18px', fontSize: '16px', display: 'block' }}
					>
						<Trans
							i18nKey="daysLeftKickOffText"
							values={{
								days: DAYS - days,
								textDays: DAYS - days === 1 ? t.dayText : t.daysText,
							}}
						/>
					</Typography.Text>
				</div>
			) : isBasic ? (
				<>
					<div className={s.loyaltyRow}>
						{statusLabel}
						<div className={s.loyaltyIcon}>
							<BasicIcon />
						</div>
					</div>
					<Typography.Text style={{ fontSize: '16px', display: 'block', marginBottom: 16 }}>
						{t.loyaltyStatusBasicText}
					</Typography.Text>
				</>
			) : (
				<>
					{Icon && (
						<div className={s.loyaltyRow}>
							{statusLabel}
							<div className={s.loyaltyIcon}>
								<Icon />
							</div>
						</div>
					)}

					{text && (
						<Typography.Text style={{ fontSize: '16px', display: 'block', marginBottom: 16 }}>
							{text}
						</Typography.Text>
					)}

					<ProgressComponent percent={percent} />
					<div className={s.progressInfo}>
						<div>
							<Typography.Text>€{balance}</Typography.Text>
							&nbsp;/&nbsp;
							<Typography.Text>
								€{isPlatinum ? plan?.minAmount : nextPlan?.minAmount}
							</Typography.Text>
						</div>

						{nextPlan && !isPlatinum && (
							<Typography.Text>
								{nextPlan?.title} (+{nextPlan?.percent}%)
							</Typography.Text>
						)}
					</div>
				</>
			)}

			<Typography.Text type="secondary" className={s.readMore}>
				<a
					href={Utils.getLinkLanguage(
						'help-category/operating-principles-of-the-loyalty-program',
						'lt/duk-category/kaip-veikia-lojalumo-programa',
					)}
					className={s.link}
					target="_blank"
					rel="noreferrer"
				>
					{t.readMoreLoyaltyText}
				</a>
			</Typography.Text>
		</SettingsCard>
	);
};
